import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import { AcademicCapIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';
import LearningPathCard from '../components/learning/LearningPathCard';

const Learning = () => {
    const [loading, setLoading] = useState(true);
    const [pathData, setPathData] = useState(null);
    const [, setCategories] = useState([]);
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [pathRes, categoriesRes] = await Promise.all([
                axios.get('/learning/path'),
                axios.get('/learning/categories')
            ]);
            setPathData(pathRes.data.data);
            setCategories(categoriesRes.data.data);
        } catch (error) {
            toast.error('Failed to load learning data');
        } finally {
            setLoading(false);
        }
    };

    const handleSync = async () => {
        try {
            await axios.post('/learning/sync');
            toast.success('Learning assignments synced');
            loadData();
        } catch (error) {
            toast.error('Failed to sync');
        }
    };

    if (loading) {
        return (
            <div className="space-y-4" role="status" aria-live="polite">
                <div className="skeleton h-10 w-64" />
                <div className="skeleton h-40" />
                <div className="skeleton h-12" />
                <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="skeleton h-24" />
                    ))}
                </div>
                <span className="sr-only">Loading your learning path…</span>
            </div>
        );
    }

    const items = pathData?.items || [];
    const progress = pathData?.progress || {};
    const isCatalog = pathData?.viewMode === 'catalog';

    const filteredItems = isCatalog
        ? items
        : filter === 'all'
            ? items
            : items.filter(item => {
                if (filter === 'available') return item.status === 'available' || item.status === 'in_progress';
                if (filter === 'completed') return item.status === 'completed';
                if (filter === 'locked') return item.status === 'locked';
                return true;
            });

    return (
        <div className="w-full space-y-6">
            <PageHeader
                title="Learning & Training"
                description={
                    isCatalog
                        ? 'Browse the default internship learning path and modules assigned to interns'
                        : 'Follow your guided learning path'
                }
                actions={
                    !isCatalog ? (
                        <button type="button" onClick={handleSync} className="btn-secondary">
                            <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
                            Sync
                        </button>
                    ) : null
                }
            />

            <div className="card animate-fade-up">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-3 bg-brand-50/60 rounded-lg text-center border border-brand-100">
                        <p className="text-xs font-semibold uppercase tracking-wider text-ink-500">Total</p>
                        <p className="mt-1 text-xl font-bold text-ink-950 tabular-nums">{progress.total || 0}</p>
                    </div>
                    {isCatalog ? (
                        <>
                            <div className="p-3 bg-amber-50 rounded-lg text-center border border-amber-100">
                                <p className="text-xs font-semibold uppercase tracking-wider text-ink-500">Mandatory</p>
                                <p className="mt-1 text-xl font-bold text-amber-800 tabular-nums">{progress.mandatory || 0}</p>
                            </div>
                            <div className="p-3 bg-sky-50 rounded-lg text-center border border-sky-100">
                                <p className="text-xs font-semibold uppercase tracking-wider text-ink-500">Path</p>
                                <p className="mt-1 text-sm font-bold text-sky-800 truncate px-1">{pathData?.path?.name || 'Default'}</p>
                            </div>
                            <div className="p-3 bg-ink-50 rounded-lg text-center border border-ink-100">
                                <p className="text-xs font-semibold uppercase tracking-wider text-ink-500">Est. hours</p>
                                <p className="mt-1 text-xl font-bold text-ink-950 tabular-nums">{pathData?.path?.estimated_duration_hours || 0}</p>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="p-3 bg-emerald-50 rounded-lg text-center border border-emerald-100">
                                <p className="text-xs font-semibold uppercase tracking-wider text-ink-500">Completed</p>
                                <p className="mt-1 text-xl font-bold text-emerald-800 tabular-nums">{progress.completed || 0}</p>
                            </div>
                            <div className="p-3 bg-sky-50 rounded-lg text-center border border-sky-100">
                                <p className="text-xs font-semibold uppercase tracking-wider text-ink-500">In Progress</p>
                                <p className="mt-1 text-xl font-bold text-sky-800 tabular-nums">{progress.inProgress || 0}</p>
                            </div>
                            <div className="p-3 bg-ink-50 rounded-lg text-center border border-ink-100">
                                <p className="text-xs font-semibold uppercase tracking-wider text-ink-500">Available</p>
                                <p className="mt-1 text-xl font-bold text-ink-950 tabular-nums">{progress.available || 0}</p>
                            </div>
                        </>
                    )}
                </div>

                {!isCatalog && (
                    <div className="mt-5">
                        <div className="flex justify-between text-sm text-ink-600">
                            <span>Overall Progress</span>
                            <span className="font-semibold tabular-nums">{progress.percentage || 0}%</span>
                        </div>
                        <div className="progress-track mt-1.5" aria-hidden="true">
                            <div
                                className="progress-fill"
                                style={{ width: `${progress.percentage || 0}%` }}
                            />
                        </div>
                    </div>
                )}
            </div>

            {!isCatalog && (
                <div className="card !py-3 animate-fade-up">
                    <div className="flex flex-wrap gap-2">
                        {['all', 'available', 'completed', 'locked'].map((filterOption) => (
                            <button
                                key={filterOption}
                                type="button"
                                onClick={() => setFilter(filterOption)}
                                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                                    filter === filterOption
                                        ? 'bg-brand-700 text-white'
                                        : 'bg-ink-100 text-ink-600 hover:bg-ink-200'
                                }`}
                            >
                                {filterOption.charAt(0).toUpperCase() + filterOption.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="space-y-3">
                {filteredItems.length === 0 ? (
                    <EmptyState
                        icon={AcademicCapIcon}
                        title={isCatalog ? 'No modules in this path' : 'No items in this filter'}
                        description={
                            isCatalog
                                ? 'Add learning modules to the default path to populate this view.'
                                : 'Try another filter or sync your learning assignments.'
                        }
                    />
                ) : (
                    filteredItems.map(item => (
                        <LearningPathCard key={item.id} item={item} />
                    ))
                )}
            </div>

            {!isCatalog && pathData?.nextItem && (
                <div className="card-muted animate-fade-up">
                    <p className="text-sm text-brand-900">
                        Next step: <strong>{pathData.nextItem.title}</strong>
                    </p>
                    <Link
                        to={`/learning/item/${pathData.nextItem.id}`}
                        className="mt-2 inline-flex text-sm font-semibold text-brand-700 hover:text-brand-800"
                    >
                        Continue →
                    </Link>
                </div>
            )}
        </div>
    );
};

export default Learning;
